import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { couplingAnalysisAPI } from '../../services/api'
import DataReadinessCard from './DataReadinessCard'
import RelationView from './RelationView'
import LoopsList from './LoopsList'
import CouplingTable from './CouplingTable'
import WeekdayTable from './WeekdayTable'
import TimelineSection from './TimelineSection'
import CompletenessNotice from './CompletenessNotice'

const POLL_MS = 2000
const LATEST_KEY = ['coupling-analysis', 'latest']
const isActive = (status) => status === 'pending' || status === 'running'

/** VH-222: how the five domains connect — associations and time order only. */
export default function SystemViewTab() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [runId, setRunId] = useState(null)
  const [readiness, setReadiness] = useState(null)

  const latest = useQuery({
    queryKey: LATEST_KEY,
    queryFn: () => couplingAnalysisAPI.getLatest().then((r) => r.data)
      .catch((err) => { if (err?.response?.status === 404) return null; throw err }),
  })

  const run = useQuery({
    queryKey: ['coupling-analysis', 'run', runId],
    queryFn: () => couplingAnalysisAPI.getRun(runId).then((r) => r.data),
    enabled: runId != null,
    refetchInterval: (q) => (isActive(q.state.data?.status) || !q.state.data ? POLL_MS : false),
  })
  const runStatus = run.data?.status

  useEffect(() => {
    if (runStatus === 'completed') {
      queryClient.invalidateQueries({ queryKey: LATEST_KEY })
      setRunId(null)
    }
  }, [runStatus, queryClient])

  const start = useMutation({
    mutationFn: () => couplingAnalysisAPI.start().then((r) => r.data),
    onMutate: () => setReadiness(null),
    onSuccess: (data) => setRunId(data.id),
    onError: (err) => {
      const { status, data } = err?.response || {}
      if (status === 409 && data?.run) setRunId(data.run.id)
      else if (status === 422 && data?.readiness) setReadiness(data.readiness)
    },
  })
  const startFailed = start.isError && ![409, 422].includes(start.error?.response?.status)
  const running = start.isPending || (runId != null && runStatus !== 'failed')

  const data = latest.data
  const result = data?.result
  const failed = new Set(result?.failed_sections || [])

  return (
    <section className="space-y-5" data-testid="system-view-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">{t('systemView.title')}</h2>
          <p className="text-sm text-gray-600">{t('systemView.subtitle')}</p>
        </div>
        <button type="button" onClick={() => start.mutate()} disabled={running}
          className="px-3 py-1.5 text-sm rounded bg-primary text-white disabled:opacity-50" data-testid="run-analysis">
          {running ? t('systemView.running') : result ? t('systemView.rerun') : t('systemView.run')}
        </button>
      </div>

      {running && <p className="text-sm text-gray-600" role="status" data-testid="run-in-progress">{t('systemView.inProgress')}</p>}
      {runStatus === 'failed' && <p className="text-sm text-red-600" role="alert" data-testid="run-failed">{t('systemView.runFailed')}</p>}
      {startFailed && <p className="text-sm text-red-600" role="alert">{t('systemView.startFailed')}</p>}
      {readiness && <DataReadinessCard readiness={readiness} />}

      {latest.isLoading && <div className="h-48 bg-surface-container border rounded-lg animate-pulse" />}
      {latest.isError && (
        <div className="border border-red-300 rounded-lg p-3 text-sm" role="alert">
          <p>{t('systemView.loadFailed')}</p>
          <button type="button" onClick={() => latest.refetch()} className="mt-1 underline">{t('systemView.retry')}</button>
        </div>
      )}
      {latest.isSuccess && !result && !readiness && (
        <p className="text-sm text-gray-600" data-testid="no-result">{t('systemView.empty')}</p>
      )}

      {result && (
        <>
          <p className="text-xs text-gray-600" data-testid="period">
            {t('systemView.period', { from: data.period_start || result.period_start, to: data.period_end || result.period_end, computed: (data.completed_at || '').slice(0, 10) })}
          </p>
          {result.sufficient_data === false && <DataReadinessCard readiness={result.readiness} />}
          {failed.size > 0 && (
            <p className="text-sm text-amber-700" role="alert" data-testid="sections-failed">{t('systemView.sectionsFailed')}</p>
          )}
          {result.summary && <p className="text-sm border-l-4 border-primary pl-3">{result.summary}</p>}
          <CompletenessNotice completeness={result.completeness} />
          <RelationView view={result.relation_view} />
          <LoopsList loops={result.loops} />
          <CouplingTable couplings={result.couplings} />
          <WeekdayTable rows={result.weekday} />
          <TimelineSection timeline={result.timeline} />
        </>
      )}
    </section>
  )
}
